/**
 * How `SearchableSelect` / `SearchableMultiSelect` name their TRIGGER
 * (#16638, approved in #16658).
 *
 * WCAG 2.5.3 (Label in Name), which axe-core 4.14 grades as
 * `label-content-name-mismatch`: a control's accessible name must contain
 * the text it shows. An overriding `aria-label="Who to ask"` on a trigger
 * that shows "2 selected" fails it, and a speech-input user who says what
 * they see cannot reach the control.
 *
 * So the trigger is named by `aria-labelledby` = a LABEL id followed by the
 * id of {@link TriggerValue}, the span wrapping what the trigger shows (its
 * placeholder, its "N selected" count, or the chosen item). The name reads
 * "Who to ask 2 selected". Never a composed `aria-label` string: that would
 * restate the visible text and drift from it.
 *
 * The label id is the caller's visible label (`ariaLabelledBy`) when it has
 * one. The two components differ for a caller with only `ariaLabel`:
 *
 * - `SearchableMultiSelect` renders that text in a {@link TriggerNameText}
 *   `hidden` span and points the reference at it (accname reads a hidden
 *   node when an `aria-labelledby` names it directly), so its name is still
 *   "<ariaLabel> <what it shows>".
 * - `SearchableSelect` keeps the overriding `aria-label` for such callers
 *   ({@link triggerNameProps}), so their names are unchanged. Only callers
 *   that pass `ariaLabelledBy` (the meeting Home picker) get the
 *   label-plus-value name; moving the rest over changes every caller's name
 *   and is left to a follow-up.
 *
 * `ariaLabel` also keeps naming the option LISTBOX
 * (`./searchable-select-listbox-name`), which is why a caller with a visible
 * label may pass both.
 *
 * Shared here, like the sibling `searchable-select-*` modules, because the
 * two components are two files (#13473) and use the same ids and spans.
 */

import type { ReactNode } from 'react'

/** The id of the span wrapping the trigger's visible value. */
export function triggerValueId(baseId: string): string {
  return `${baseId}-value`
}

/** The id of the hidden span carrying an `ariaLabel`-only caller's text. */
export function triggerNameTextId(baseId: string): string {
  return `${baseId}-name`
}

/** The trigger's `aria-labelledby`: the label, then the visible value. */
export function triggerLabelledBy(
  labelId: string | undefined,
  valueId: string,
): string | undefined {
  return labelId ? `${labelId} ${valueId}` : undefined
}

/**
 * The trigger's `aria-label` / `aria-labelledby` pair. With a visible label
 * (`ariaLabelledBy`) the name is that label plus the visible value, and no
 * `aria-label` overrides it; without one, `ariaLabel` is passed through
 * unchanged — `SearchableSelect`'s existing callers keep their names.
 */
export function triggerNameProps(
  ariaLabel: string | undefined,
  ariaLabelledBy: string | undefined,
  valueId: string,
): { 'aria-label'?: string; 'aria-labelledby'?: string } {
  if (ariaLabelledBy) {
    return { 'aria-labelledby': triggerLabelledBy(ariaLabelledBy, valueId) }
  }
  return { 'aria-label': ariaLabel }
}

/**
 * Wraps the trigger's visible value so `aria-labelledby` can reference it:
 * `children` when there is a selection to show, otherwise the placeholder.
 * Lays its children out as the trigger itself does, so a `renderSelected`
 * that relies on `flex-1 truncate` or on the trigger's gap is unaffected.
 */
export function TriggerValue({
  id,
  placeholder,
  children,
}: {
  id: string
  placeholder: string
  children: ReactNode
}) {
  return (
    <span id={id} className="flex min-w-0 flex-1 items-center gap-2">
      {children ?? (
        <span className="flex-1 truncate text-text-secondary">
          {placeholder}
        </span>
      )}
    </span>
  )
}

/** A label-less caller's `ariaLabel`, referenced but never shown. */
export function TriggerNameText({ id, text }: { id: string; text: string }) {
  return (
    <span id={id} hidden>
      {text}
    </span>
  )
}
